import { colors } from '@/theme/colors';
import { Link } from 'react-router';
import { ReactNode, useEffect, useMemo } from 'react';
import Cancel from '@/icons/cancel';
import Spacer from './spacer';
import { Button } from '@/shadcn-ui/button';
import BannerError from '@/icons/banner-error';
import BannerWarning from '@/icons/banner-warning';
import BannerSuccess from '@/icons/banner-success';
import BannerInfo from '@/icons/banner-info';
import { cn } from '@/shadcn-ui/utils';
import InfoSmall from '@/icons/info-small';
import CheckThick from '@/icons/check-thick';
import AlertSmall from '@/icons/alert-small';
import { isReactNode } from '../utils/isReactNode';
import { useDisableSpaNavigation } from '@/util-react-router/hooks/use-disable-spa-navigation';

export type StatusBannerVariantType =
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'custom'
  | 'default';

export interface BannerMessage {
  /** Text of button */
  text: string | ReactNode;
  /** URI or URL button for 'Learn More' */
  link?: string;
  /** Custom Text for the anchor/href*/
  linkText?: string;
}

/** Determines type of button used in banner */
export interface BannerLinkButton {
  /** Text of button */
  text: string;
  /** Configure link for banner */
  link?: {
    /** URI or URL button goes to */
    url: string;
    /** Use internal react router or anchor */
    type: 'new-tab' | 'default';
    /** Display link as Link or Button component */
    style: 'link' | 'button';
    /** Should not show arrow next to link */
    noArrow?: boolean;
    /** Override option for link to reload document (if not specified, we fall back to the SPA navigation feature flag value). */
    forceReloadDocument?: boolean;
  };
  /** Configure button for banner */
  button?: {
    type: 'primary' | 'secondary';
    onClick: () => void;
  };
}

export interface IconConfiguration {
  /** Should hide icon from banner */
  shouldHide?: boolean;
  /** Icon to be displayed next to title */
  overrideIcon?: ReactNode;
}

const createButton = (button: BannerLinkButton) => {
  return (
    <Button variant={button.button?.type} onClick={button.button?.onClick}>
      {button.text}
    </Button>
  );
};

const createLink = ({
  button,
  compact,
  primaryLinkClassName,
  reloadDocument = true,
}: {
  button: BannerLinkButton;
  compact?: boolean;
  reloadDocument?: boolean;
  primaryLinkClassName?: string;
}) => {
  const linkText = button.link?.noArrow ? `${button.text}` : `${button.text} ->`;
  const textContainer =
    button.link?.style === 'link' ? (
      linkText
    ) : (
      <Button variant="secondary">{button.text}</Button>
    );

  const linkClassName = cn(
    {
      'text-flexcar-blue40 hover:text-flexcar-blue40': button?.link?.style === 'link',
      'link-body2': compact && button?.link?.style === 'link',
      'link-body': !compact && button?.link?.style === 'link',
    },
    primaryLinkClassName
  );

  switch (button.link?.type) {
    case 'default':
      return (
        <Link
          to={button.link.url}
          className={linkClassName}
          // Only allow override if reloadDocument is false. If it's true, it means SPA navigation should be disabled system-wide, so
          // having the override switch it to false could break this link because we'd be trying to do SPA navigation when the flag
          // for that is not enabled and the system assumes there won't be any SPA navigation.
          reloadDocument={
            !reloadDocument
              ? (button.link.forceReloadDocument ?? reloadDocument)
              : reloadDocument
          }
        >
          {textContainer}
        </Link>
      );

    case 'new-tab':
      return (
        <Link
          to={button.link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
        >
          {textContainer}
        </Link>
      );
  }
};
/** Creates react router link or anchor depending on button config */
const createLinkButton = ({
  button,
  compact,
  reloadDocument = true,
  primaryLinkClassName,
}: {
  button: BannerLinkButton;
  reloadDocument?: boolean;
  compact?: boolean;
  primaryLinkClassName?: string;
}) => {
  if (button.link) {
    return createLink({ button, compact, reloadDocument, primaryLinkClassName });
  }

  if (button.button) {
    return createButton(button);
  }

  return undefined;
};

const createMessage = (
  message?: BannerMessage | BannerMessage[],
  compact?: boolean
): ReactNode => {
  if (!message) {
    return null;
  }
  if (Array.isArray(message)) {
    return (
      <ul className="m-0">
        {message.map((item, index) => {
          return (
            <li key={index}>
              <p
                className={cn({
                  body2: compact,
                  body: !compact,
                })}
              >
                {item.text}
                {item.link ? (
                  <a
                    href={`${item.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-[4px] inline-block"
                  >
                    {item.linkText ?? 'Learn More'}
                  </a>
                ) : null}
              </p>
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <>
      <p
        className={cn({
          body2: compact,
          body: !compact,
        })}
      >
        {message.text}
        {message.link ? (
          <a
            href={`${message.link}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-[4px] inline-block"
          >
            {message.linkText ?? 'Learn More'}
          </a>
        ) : null}
      </p>
    </>
  );
};

export interface StatusBannerClosable {
  isClosed: boolean;
  onClickDelegate: () => void;
  icon?: ReactNode;
}

export interface StatusBannerProps {
  /** Determines styling of banner */
  variant?: StatusBannerVariantType;
  title?: string | ReactNode;
  message?: BannerMessage | BannerMessage[] | ReactNode;
  primaryLinkButton?: BannerLinkButton;
  secondaryLinkButton?: BannerLinkButton;
  icon?: IconConfiguration;
  /** Should scroll to top of screen when rendered */
  shouldScrollToTop?: boolean;
  /** Should banner be closable */
  closableConfiguration?: StatusBannerClosable;
  compact?: boolean;
  /** Should links be below message or to the right of message? */
  linkLayoutVariant?: 'default' | 'row-right';
  className?: string;
  bodyClassName?: string;
  iconContainerClassName?: string;
  primaryLinkClassName?: string;
}

/**
 * Displays an error banner with the provided title and message.
 * Supports up to two optional actions, one rendered as a hyperlink
 * and the other rendered as a button. If appLinkText and appLink
 * are provided, the hyperlink is rendered. If buttonText and
 * buttonLink are provided, the button is rendered.
 *
 * Can be configured via closable object to hide/show
 */
export default function StatusBanner({
  variant,
  title,
  message,
  primaryLinkButton,
  secondaryLinkButton,
  icon,
  shouldScrollToTop = false,
  closableConfiguration,
  compact = false,
  linkLayoutVariant: buttonLayoutVariant = 'default',
  className,
  bodyClassName,
  iconContainerClassName,
  primaryLinkClassName,
}: StatusBannerProps) {
  const hasLinkButton = primaryLinkButton || secondaryLinkButton;
  /** If closable, listen to prop otherwise is not hidden */
  const isHidden = closableConfiguration?.isClosed ?? false;
  const hasTitleAndMessage = title && message;

  const backgroundColor = useMemo(() => {
    switch (variant) {
      case 'error':
        return colors.red90;
      case 'warning':
        return colors.yellow90;
      case 'success':
        return colors.green90;
      case 'info':
        return colors.blue90;
      case 'custom':
        return undefined; // we expect the background color to be defined elsewhere so we don't set it here
      default:
        return colors.neutral90;
    }
  }, [variant]);

  const borderColor = useMemo(() => {
    switch (variant) {
      case 'error':
        return colors.red70;
      case 'warning':
        return colors.yellow70;
      case 'success':
        return colors.green70;
      case 'info':
        return colors.blue70;
      case 'custom':
        return undefined; // we expect the border color to be defined elsewhere so we don't set it here
      default:
        return colors.neutral100;
    }
  }, [variant]);

  const titleIcon = useMemo(() => {
    if (icon?.shouldHide) {
      return undefined;
    }

    if (icon?.overrideIcon) {
      return icon.overrideIcon;
    }

    const isNonInfoVariant =
      variant === 'error' || variant === 'warning' || variant === 'success';

    // NOTE: negative top margin is to offset padding added by a glow on the Banner icons. the Info icon is also
    // slightly smaller than the other icons so it needs slightly different adjustments.
    const leftMarginAdjustment = isNonInfoVariant ? 'ml-[-9px]' : 'ml-[-7px]';
    const topMarginAdjustment = isNonInfoVariant ? 'mt-[-8px]' : 'mt-[-6px]';
    const iconSizeClassName = compact
      ? 'flex-shrink-0 h-[22px] w-[30px] mt-[-2px] ml-[-8px] mr-[-8px]'
      : `flex-shrink-0 ${leftMarginAdjustment} ${hasTitleAndMessage ? topMarginAdjustment : ''}`;

    switch (variant) {
      case 'error':
        return compact ? (
          <AlertSmall
            fillPrimary={colors.neutral1}
            className={cn({
              'mt-[2px]': hasTitleAndMessage && !hasLinkButton,
              'mt-[1px]': hasLinkButton,
            })}
          />
        ) : (
          <BannerError className={iconSizeClassName} />
        );
      case 'warning':
        return compact ? (
          <AlertSmall
            fillPrimary={colors.neutral1}
            className={cn({
              'mt-[2px]': hasTitleAndMessage && !hasLinkButton,
              'mt-[1px]': hasLinkButton,
            })}
          />
        ) : (
          <BannerWarning className={iconSizeClassName} />
        );
      case 'success':
        return compact ? (
          <CheckThick
            className={cn({
              'mt-[2px]': hasTitleAndMessage && !hasLinkButton,
              'mt-[1px]': hasLinkButton,
            })}
          />
        ) : (
          <BannerSuccess className={iconSizeClassName} />
        );
      case 'info':
        return compact ? (
          <InfoSmall
            fill={colors.neutral1}
            className={cn('h-[16px] w-[16px]', {
              'mt-[1px]': hasTitleAndMessage || hasLinkButton,
              'mb-[3px] mt-0': !hasTitleAndMessage && !hasLinkButton,
            })}
          />
        ) : (
          <BannerInfo className={iconSizeClassName} />
        );
      default:
        return <BannerInfo className={iconSizeClassName} />;
    }
  }, [compact, icon, hasTitleAndMessage, hasLinkButton, variant]);

  useEffect(() => {
    if (shouldScrollToTop) {
      window.scrollTo(0, 0);
    }
  }, [shouldScrollToTop]);

  const { disableSpaNavigation } = useDisableSpaNavigation();

  if (isHidden) return;

  return (
    <div
      id={`${variant}-status-banner`}
      data-testid={`${variant}-status-banner`}
      className={cn(
        'w-full rounded-[16px]',
        {
          'border-[1.5px] border-solid bg-flexcar-neutral100 p-[24px]': !compact,
          'border-none px-[16px] py-[12px]': compact,
        },
        className
      )}
      style={{
        borderColor,
        background: backgroundColor,
      }}
    >
      <div className={cn('flex flex-row gap-[8px]', bodyClassName)}>
        {titleIcon ? (
          <div
            className={cn(
              'flex flex-col',
              {
                'justify-start': hasTitleAndMessage || hasLinkButton,
                'justify-center': !hasTitleAndMessage && !hasLinkButton,
              },
              iconContainerClassName
            )}
          >
            {titleIcon}
          </div>
        ) : null}

        <div
          className={cn('flex flex-col', {
            'justify-start': hasTitleAndMessage || hasLinkButton,
            'justify-center': !hasTitleAndMessage && !hasLinkButton,
          })}
        >
          {title ? (
            isReactNode(title) ? (
              title
            ) : (
              <p
                className={cn({
                  'body-strong': !compact,
                  'body2-strong': compact,
                })}
              >
                {title}
              </p>
            )
          ) : null}
          {hasTitleAndMessage ? <Spacer variant="height4" /> : null}

          {isReactNode(message) ? message : createMessage(message, compact)}

          {hasLinkButton && buttonLayoutVariant === 'default' ? (
            <>
              {compact ? <Spacer variant="height8" /> : <Spacer variant="height16" />}
              <Spacer variant="width16" />
              <div
                className={
                  'flex flex-col justify-start gap-2 sm:flex-row sm:items-center sm:gap-1'
                }
              >
                {primaryLinkButton ? (
                  <div className="flex items-center">
                    {createLinkButton({
                      button: primaryLinkButton,
                      compact,
                      reloadDocument: disableSpaNavigation,
                      primaryLinkClassName,
                    })}
                    <Spacer variant="height16" />
                    <Spacer variant="width16" />
                  </div>
                ) : null}
                {secondaryLinkButton ? (
                  <>
                    {createLinkButton({
                      button: secondaryLinkButton,
                      compact,
                      reloadDocument: disableSpaNavigation,
                    })}
                  </>
                ) : null}
              </div>
            </>
          ) : null}
        </div>

        {hasLinkButton && buttonLayoutVariant === 'row-right' ? (
          <div className={'ml-auto flex flex-row items-center gap-[16px]'}>
            {primaryLinkButton
              ? createLinkButton({
                  button: primaryLinkButton,
                  compact,
                  reloadDocument: disableSpaNavigation,
                })
              : null}
            {secondaryLinkButton
              ? createLinkButton({
                  button: secondaryLinkButton,
                  compact,
                  reloadDocument: disableSpaNavigation,
                })
              : null}
          </div>
        ) : null}
        {closableConfiguration?.onClickDelegate ? (
          closableConfiguration.icon ? (
            <button
              className={`ml-auto h-fit w-fit flex-shrink-0 cursor-pointer`}
              onClick={closableConfiguration.onClickDelegate}
            >
              {closableConfiguration.icon}
            </button>
          ) : (
            <button
              className={`ml-auto h-[16px] w-[16px] flex-shrink-0 cursor-pointer`}
              onClick={closableConfiguration.onClickDelegate}
            >
              <Cancel />
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}
